import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CustomSelect from '../components/CustomSelect';
import { lojasService, posicaoEstoqueService } from '../services/api';
import { usuarioTemPosicaoEstoquesRestrita } from '../utils/loginAccess';
import '../styles/ConsultaGlobal.css';
import '../styles/PosicaoEstoques.css';

const TIPOS_PERMITIDOS = ['admin', 'supervisor', 'lider', 'gerente', 'sub', 'subfarma'];
const TIPOS_TODAS_FILIAIS = ['admin', 'supervisor'];
const PERMISSAO_ESTOQUE = 2;
const PERMISSAO_TOTAL = 15;
const MONTHS = [
  { key: '01', label: 'JAN' },
  { key: '02', label: 'FEV' },
  { key: '03', label: 'MAR' },
  { key: '04', label: 'ABR' },
  { key: '05', label: 'MAI' },
  { key: '06', label: 'JUN' },
  { key: '07', label: 'JUL' },
  { key: '08', label: 'AGO' },
  { key: '09', label: 'SET' },
  { key: '10', label: 'OUT' },
  { key: '11', label: 'NOV' },
  { key: '12', label: 'DEZ' }
];

function parseNumero(value) {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const normalizado = String(value).trim().replace(/\./g, '').replace(',', '.');
  const parsed = Number.parseFloat(normalizado);
  return Number.isFinite(parsed) ? parsed : 0;
}

function money(value) {
  return parseNumero(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function integer(value) {
  return Number(parseNumero(value) || 0).toLocaleString('pt-BR');
}

function formatPercent(value) {
  return `${parseNumero(value).toFixed(2).replace('.', ',')} %`;
}

function formatCompactNumber(value) {
  const numero = parseNumero(value);
  return numero.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}

function formatData(value) {
  if (!value) return '-';
  if (typeof value === 'string' && value.includes('T')) {
    const [datePart] = value.split('T');
    const [year, month, day] = datePart.split('-');
    if (year && month && day) return `${day}/${month}/${String(year).slice(-2)}`;
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${String(year).slice(-2)}`;
  }
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return String(value);
  return `${String(data.getDate()).padStart(2, '0')}/${String(data.getMonth() + 1).padStart(2, '0')}/${String(data.getFullYear()).slice(-2)}`;
}

function formatEmbalagem(produto) {
  const qtdCom = parseNumero(produto?.QTDCOM || 0);
  const qtdExp = parseNumero(produto?.QTDEXP || 0);
  return `CX - ${integer(qtdCom)}  Exp. - ${integer(qtdExp)}`;
}

function monthLabel(month) {
  return `${month.label}/${String(month.year).slice(-2)}`;
}

function readHistValue(histByYear, year, prefix, monthKey) {
  return histByYear?.[String(year)]?.[`${prefix}${monthKey}`] ?? 0;
}

function monthCell(histByYear, month) {
  const saida = Number(readHistValue(histByYear, month.year, 'SAI', month.key) || 0);
  const preco = Number(readHistValue(histByYear, month.year, 'PRECO', month.key) || 0);
  return `${saida} / ${money(preco)}`;
}

function pareceCodigoOuBarras(value) {
  return /^\d+$/.test(String(value || '').trim());
}

function calcularDemandaPeriodo(row) {
  const demandaMensal = parseNumero(row?.DEMMEN || 0);
  if (demandaMensal <= 0) return 0;
  const curva = String(row?.ABC || '').trim().toUpperCase();
  const diasPorCurva = {
    AA: 8,
    A: 8,
    BA: 15,
    B: 15,
    CA: 24,
    C: 20
  };
  return Math.round((demandaMensal / 30) * (diasPorCurva[curva] || 8));
}

function somarLinhas(rows) {
  const base = {
    OBJETIVOMES: 0,
    MINPRO: 0,
    ESTMIN: 0,
    ESTOQ: 0,
    ESTOQUE_ROBO: 0,
    DEMMEN: 0,
    DPER: 0,
    QTDPED: 0,
    QTDENT: 0,
    QTDTRN: 0,
    RUPT_S: 0
  };

  rows.forEach((row) => {
    base.OBJETIVOMES += parseNumero(row?.OBJETIVOMES || 0);
    base.MINPRO += parseNumero(row?.MINPRO || 0);
    base.ESTMIN += parseNumero(row?.ESTMIN || 0);
    base.ESTOQ += parseNumero(row?.ESTOQ || 0);
    base.ESTOQUE_ROBO += parseNumero(row?.ESTOQUE_ROBO || 0);
    base.DEMMEN += parseNumero(row?.DEMMEN || 0);
    base.DPER += calcularDemandaPeriodo(row);
    base.QTDPED += parseNumero(row?.QTDPED || 0);
    base.QTDENT += parseNumero(row?.QTDENT || 0);
    base.QTDTRN += parseNumero(row?.QTDTRN || 0);
    if (String(row?.ESTAEMRUPTURA || '').toUpperCase() === 'S') base.RUPT_S += 1;
  });

  return base;
}

function statusPedidoDeposito(status) {
  const key = String(status ?? '').trim();
  const map = {
    0: 'ABERTO',
    1: 'FATURADO',
    2: 'CANCELADO',
    3: 'RECEBIDO'
  };
  return map[key] || key || '-';
}

function detalheFillRows(colCount, usedRows, minRows = 16) {
  const missing = Math.max(0, minRows - usedRows);
  return Array.from({ length: missing }, (_, rowIndex) => (
    <tr key={`fill-${colCount}-${rowIndex}`} className="posicao-detalhes-fill-row" aria-hidden="true">
      {Array.from({ length: colCount }, (_, colIndex) => (
        <td key={`fill-${rowIndex}-${colIndex}`}>&nbsp;</td>
      ))}
    </tr>
  ));
}

function PosicaoDetalhesTransito({ detalhe, abaAtiva, setAbaAtiva, loading, onClose }) {
  if (!detalhe || typeof document === 'undefined') return null;

  const tabs = [
    { key: 'notas', label: 'Notas' },
    { key: 'pedidos', label: 'Pedidos' },
    { key: 'conferencias', label: 'Conferências' },
    { key: 'deposito', label: 'Pedidos Depósito' }
  ];

  const renderEmpty = (colCount) => (
    <>
      <tr className="posicao-detalhes-fill-row">
        {Array.from({ length: colCount }, (_, colIndex) => <td key={`empty-${colIndex}`}>&nbsp;</td>)}
      </tr>
      {detalheFillRows(colCount, 1)}
    </>
  );

  return createPortal(
    <div className="posicao-detalhes-backdrop" onMouseDown={onClose}>
      <section className="posicao-detalhes-window" role="dialog" aria-modal="true" aria-label="Notas em Trânsito" onMouseDown={(e) => e.stopPropagation()}>
        <header className="posicao-detalhes-titlebar">
          <div className="posicao-detalhes-dots">
            <button type="button" className="red" aria-label="Fechar" onClick={onClose}></button>
            <span></span>
            <span></span>
          </div>
          <strong>Notas em Trânsito</strong>
        </header>

        <div className="posicao-detalhes-body">
          <h3>{detalhe.produtoNome || `Produto ${detalhe.produto}`}</h3>
          <nav className="posicao-detalhes-tabs" aria-label="Abas de detalhes">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={abaAtiva === tab.key ? 'active' : ''}
                onClick={() => setAbaAtiva(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="posicao-detalhes-table-frame">
            {abaAtiva === 'notas' && (
              <table className="posicao-detalhes-grid">
                <thead>
                  <tr>
                    <th>N° NFe</th>
                    <th>Fornecedor</th>
                    <th>Quant.</th>
                    <th>Vlr.Unitário</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? renderEmpty(5) : detalhe.notas.length ? (
                    <>
                      {detalhe.notas.map((item, index) => (
                        <tr key={`${item.NRNOTA}-${item.CDFORNE}-${index}`}>
                          <td>{item.NRNOTA}</td>
                          <td>{item.ABREV || '-'}</td>
                          <td>{integer(item.QTD)}</td>
                          <td>{money(item.VLRUNI)}</td>
                          <td>{formatData(item.DATA)}</td>
                        </tr>
                      ))}
                      {detalheFillRows(5, detalhe.notas.length)}
                    </>
                  ) : renderEmpty(5)}
                </tbody>
              </table>
            )}

            {abaAtiva === 'pedidos' && (
              <table className="posicao-detalhes-grid">
                <thead>
                  <tr>
                    <th>N° Ped.</th>
                    <th>ST</th>
                    <th>Fornecedor</th>
                    <th>Quant.</th>
                    <th>Vlr.Unitário</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? renderEmpty(6) : detalhe.pedidos.length ? (
                    <>
                      {detalhe.pedidos.map((item, index) => (
                        <tr key={`${item.NRPEDIDO}-${index}`}>
                          <td>{item.NRPEDIDO}</td>
                          <td>{item.PEDELETRONICO || item.PEDELE || '-'}</td>
                          <td>{item.ABREV || '-'}</td>
                          <td>{integer(item.QTD)}</td>
                          <td>{money(item.VLRPED)}</td>
                          <td>{formatData(item.DATA)}</td>
                        </tr>
                      ))}
                      {detalheFillRows(6, detalhe.pedidos.length)}
                    </>
                  ) : renderEmpty(6)}
                </tbody>
              </table>
            )}

            {abaAtiva === 'conferencias' && (
              <table className="posicao-detalhes-grid">
                <thead>
                  <tr>
                    <th>N° NFe</th>
                    <th>Fornecedor</th>
                    <th>Endereço</th>
                    <th>Quant.</th>
                    <th>Emb.</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? renderEmpty(6) : detalhe.conferencias.length ? (
                    <>
                      {detalhe.conferencias.map((item, index) => (
                        <tr key={`${item.NRNOTA}-${item.CDFORNE}-${index}`}>
                          <td>{item.NRNOTA}</td>
                          <td>{item.ABREV || '-'}</td>
                          <td>{item.ENDERECO || ''}</td>
                          <td>{integer(item.QTD)}</td>
                          <td>{integer(item.QTDEMB)}</td>
                          <td>{formatData(item.DATACONF)}</td>
                        </tr>
                      ))}
                      {detalheFillRows(6, detalhe.conferencias.length)}
                    </>
                  ) : renderEmpty(6)}
                </tbody>
              </table>
            )}

            {abaAtiva === 'deposito' && (
              <table className="posicao-detalhes-grid">
                <thead>
                  <tr>
                    <th>N° Ped.</th>
                    <th>Orig...</th>
                    <th>Data</th>
                    <th>Qtd.Ped.</th>
                    <th>Status</th>
                    <th>Qtd.Fat.</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? renderEmpty(6) : detalhe.pedidosDeposito.length ? (
                    <>
                      {detalhe.pedidosDeposito.map((item, index) => (
                        <tr key={`${item.NRPEDIDO}-${item.CDFIL}-${index}`}>
                          <td>{item.NRPEDIDO}</td>
                          <td>{String(item.CDFIL || '').padStart(3, '0')}</td>
                          <td>{formatData(item.DATA)}</td>
                          <td>{integer(item.QTD)}</td>
                          <td>{statusPedidoDeposito(item.FGSTATUS)}</td>
                          <td>{integer(item.QTDCONF)}</td>
                        </tr>
                      ))}
                      {detalheFillRows(6, detalhe.pedidosDeposito.length)}
                    </>
                  ) : renderEmpty(6)}
                </tbody>
              </table>
            )}
          </div>

          {detalhe.chaveNfe && <div className="posicao-detalhes-chave">Chave NFe: {detalhe.chaveNfe}</div>}
        </div>

        <footer className="posicao-detalhes-footer">
          <button type="button" onClick={onClose}>Fechar</button>
        </footer>
      </section>
    </div>,
    document.body
  );
}

const PosicaoEstoques = ({ userData }) => {
  const buscaProdutoRef = useRef(null);
  const [codigo, setCodigo] = useState('');
  const [filial, setFilial] = useState('TODAS');
  const [lojas, setLojas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBuscaProduto, setLoadingBuscaProduto] = useState(false);
  const [erro, setErro] = useState('');
  const [produto, setProduto] = useState(null);
  const [posicoes, setPosicoes] = useState([]);
  const [historicos, setHistoricos] = useState({});
  const [filialSelecionada, setFilialSelecionada] = useState(null);
  const [sugestoesProduto, setSugestoesProduto] = useState([]);
  const [mostrarSugestoesProduto, setMostrarSugestoesProduto] = useState(false);
  const [highlightProdutoIndex, setHighlightProdutoIndex] = useState(0);
  const [detalheTransito, setDetalheTransito] = useState(null);
  const [detalheAba, setDetalheAba] = useState('notas');
  const [detalheLoading, setDetalheLoading] = useState(false);

  const tipoUsuario = String(userData?.tipo || '').toLowerCase();
  const permissaoBruta = userData?.permissao ?? userData?.PERMISSAO;
  const permissaoParseada = Number.parseInt(permissaoBruta, 10);
  const permissaoUsuario = Number.isFinite(permissaoParseada) ? permissaoParseada : PERMISSAO_TOTAL;
  const temPermissaoExplicita = permissaoBruta !== undefined && permissaoBruta !== null && permissaoBruta !== '';
  const posicaoEstoquesRestrita = usuarioTemPosicaoEstoquesRestrita(userData);
  const podeAcessar = temPermissaoExplicita
    ? posicaoEstoquesRestrita || permissaoUsuario === PERMISSAO_ESTOQUE || permissaoUsuario === PERMISSAO_TOTAL
    : posicaoEstoquesRestrita || TIPOS_PERMITIDOS.includes(tipoUsuario);
  const podeVerTodasFiliais = !posicaoEstoquesRestrita && TIPOS_TODAS_FILIAIS.includes(tipoUsuario);
  const filialUsuario = Number(userData?.loja_id || userData?.lojaId || userData?.loja?.id || 0) || null;
  const esconderEstoque = !podeVerTodasFiliais;

  const monthColumns = useMemo(() => {
    const now = new Date();
    const seq = [];
    for (let offset = 0; offset < 6; offset += 1) {
      const current = new Date(now.getFullYear(), now.getMonth() - offset, 1);
      seq.push({
        key: String(current.getMonth() + 1).padStart(2, '0'),
        label: MONTHS[current.getMonth()].label,
        year: current.getFullYear()
      });
    }
    return seq;
  }, []);

  const anosNecessarios = useMemo(() => [...new Set(monthColumns.map((month) => month.year).filter(Number.isFinite))], [monthColumns]);

  useEffect(() => {
    const carregarLojas = async () => {
      try {
        const lojasResponse = await lojasService.getLojas();
        setLojas((lojasResponse || []).sort((a, b) => Number(a.numero || a.id || 0) - Number(b.numero || b.id || 0)));
      } catch (error) {
        console.error('Erro ao carregar lojas:', error);
      }
    };
    carregarLojas();
  }, []);

  useEffect(() => {
    if (podeVerTodasFiliais) {
      setFilial((atual) => atual || 'TODAS');
    } else if (filialUsuario) {
      setFilial(String(filialUsuario));
    }
  }, [podeVerTodasFiliais, filialUsuario]);

  useEffect(() => {
    const termo = String(codigo || '').trim();
    if (termo.length < 2 || pareceCodigoOuBarras(termo)) {
      setSugestoesProduto([]);
      setMostrarSugestoesProduto(false);
      setHighlightProdutoIndex(0);
      return;
    }

    let ativo = true;
    const timer = setTimeout(async () => {
      try {
        setLoadingBuscaProduto(true);
        const lista = await posicaoEstoqueService.buscarSugestoes(termo, 8);
        if (!ativo) return;
        setSugestoesProduto(Array.isArray(lista) ? lista : []);
        setMostrarSugestoesProduto(true);
        setHighlightProdutoIndex(0);
      } catch (_) {
        if (!ativo) return;
        setSugestoesProduto([]);
        setMostrarSugestoesProduto(false);
        setHighlightProdutoIndex(0);
      } finally {
        if (ativo) setLoadingBuscaProduto(false);
      }
    }, 250);

    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [codigo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buscaProdutoRef.current && !buscaProdutoRef.current.contains(event.target)) {
        setMostrarSugestoesProduto(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const carregarPosicaoPorCodigo = async (codigoBusca) => {
    const code = String(codigoBusca || '').trim();
    if (!code) {
      setErro('Informe o código do produto.');
      return false;
    }

    setErro('');
    setLoading(true);
    setProduto(null);
    setPosicoes([]);
    setHistoricos({});
    setFilialSelecionada(null);

    if (!podeVerTodasFiliais && !filialUsuario) {
      setErro('Não foi possível identificar a filial deste usuário.');
      setLoading(false);
      return false;
    }

    try {
      const payload = await posicaoEstoqueService.consultarCompleto({
        codigo: code,
        filialBusca: userData?.loja_id || 22,
        cdfil: podeVerTodasFiliais ? (filial || 'TODAS') : String(filialUsuario),
        anos: anosNecessarios
      });

      const listaPosicoes = Array.isArray(payload?.posicoes) ? payload.posicoes : [];
      setProduto(payload?.produto || null);
      setPosicoes(listaPosicoes);
      setHistoricos(payload?.historicos || {});

      if (!listaPosicoes.length) {
        setErro('Nenhuma posição encontrada para esse produto.');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erro ao buscar posição de estoques:', error);
      setErro(error?.message || 'Erro ao consultar posição de estoques.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const carregarPosicao = async (e) => {
    if (e) e.preventDefault();
    const code = String(codigo || '').trim();
    const ehCodigo = pareceCodigoOuBarras(code);

    if (mostrarSugestoesProduto && sugestoesProduto.length > 0) {
      const item = sugestoesProduto[Math.max(0, Math.min(highlightProdutoIndex, sugestoesProduto.length - 1))];
      if (item?.CDPRODU) {
        const ok = await carregarPosicaoPorCodigo(item.CDPRODU);
        if (ok) {
          setCodigo('');
          setSugestoesProduto([]);
          setMostrarSugestoesProduto(false);
          setHighlightProdutoIndex(0);
        }
        return;
      }
    }

    if (!ehCodigo) {
      setMostrarSugestoesProduto(true);
      return;
    }

    const ok = await carregarPosicaoPorCodigo(code);
    if (ok) {
      setCodigo('');
      setSugestoesProduto([]);
      setMostrarSugestoesProduto(false);
      setHighlightProdutoIndex(0);
    }
  };

  const resumo = useMemo(() => {
    const base = {
      totalFiliais: posicoes.length,
      estoqueTotal: 0,
      pedidoTotal: 0,
      transferenciaTotal: 0,
      rupturaTotal: 0
    };
    posicoes.forEach((row) => {
      base.estoqueTotal += Number(row?.ESTOQ || 0);
      base.pedidoTotal += Number(row?.QTDPED || 0);
      base.transferenciaTotal += Number(row?.QTDTRN || 0);
      if (String(row?.ESTAEMRUPTURA || '').toUpperCase() === 'S') base.rupturaTotal += 1;
    });
    return base;
  }, [posicoes]);

  const resumoTopo = useMemo(() => {
    const linhaBase = posicoes[0] || {};
    const precoVenda = parseNumero(produto?.PRECO || 0);
    const precoCusto = parseNumero(produto?.CUSLIQ || 0);
    const precoPromo = parseNumero(linhaBase?.PRECOPOR || 0);
    const margem = parseNumero(produto?.MARGEM || 0);
    const pmpf = parseNumero(produto?.PMPF || 0);
    const margemReal = pmpf > 0 && precoCusto > 0 ? ((pmpf - precoCusto) / pmpf) * 100 : 0;

    return {
      precoVenda,
      precoPor: precoPromo,
      precoCusto,
      margem,
      precoPromo,
      precoFabrica: parseNumero(linhaBase?.PREPRO || 0),
      custoMedio: parseNumero(produto?.CUSMED || 0),
      icms: parseNumero(produto?.ICMS || 0),
      embalagem: formatEmbalagem(produto),
      margemReal,
      pmpf,
      estoqueTotal: resumo.estoqueTotal,
      filiais: resumo.totalFiliais,
      pedidos: resumo.pedidoTotal,
      transferencias: resumo.transferenciaTotal
    };
  }, [posicoes, produto, resumo]);

  const linhaSelecionada = useMemo(() => posicoes.find((row) => String(row?.CDFIL) === String(filialSelecionada)) || null, [posicoes, filialSelecionada]);

  const gruposTabela = useMemo(() => {
    const filiaisNormais = posicoes.filter((row) => Number(row?.CDFIL || 0) < 900);
    const filiaisSeparadas = posicoes.filter((row) => Number(row?.CDFIL || 0) >= 900);
    return {
      filiaisNormais,
      filiaisSeparadas,
      totaisNormais: somarLinhas(filiaisNormais),
      totaisGerais: somarLinhas(posicoes)
    };
  }, [posicoes]);

  const lojaUsuario = useMemo(() => {
    if (!filialUsuario) return null;
    return lojas.find((loja) => Number(loja.numero || loja.id || 0) === Number(filialUsuario)) || null;
  }, [lojas, filialUsuario]);

  const abrirDetalhesTransito = useCallback(async () => {
    if (!produto?.CDPRODU || !linhaSelecionada?.CDFIL) {
      setErro('Selecione uma loja na tabela e aperte F6 para ver notas, pedidos e conferências.');
      return;
    }

    const base = {
      produto: produto.CDPRODU,
      produtoNome: produto.NOME,
      filial: linhaSelecionada.CDFIL,
      notas: [],
      pedidos: [],
      conferencias: [],
      pedidosDeposito: [],
      chaveNfe: ''
    };

    setDetalheAba('notas');
    setDetalheTransito(base);
    setDetalheLoading(true);
    setErro('');

    try {
      const data = await posicaoEstoqueService.buscarDetalhesTransito({
        codigo: produto.CDPRODU,
        filial: linhaSelecionada.CDFIL
      });

      setDetalheTransito({
        ...base,
        notas: Array.isArray(data?.notas) ? data.notas : [],
        pedidos: Array.isArray(data?.pedidos) ? data.pedidos : [],
        conferencias: Array.isArray(data?.conferencias) ? data.conferencias : [],
        pedidosDeposito: Array.isArray(data?.pedidosDeposito) ? data.pedidosDeposito : [],
        chaveNfe: data?.chaveNfe || ''
      });
    } catch (error) {
      console.error('Erro ao abrir detalhes de notas em trânsito:', error);
      setErro(error?.message || 'Erro ao buscar detalhes de notas, pedidos e conferências.');
    } finally {
      setDetalheLoading(false);
    }
  }, [linhaSelecionada, produto]);

  useEffect(() => {
    const handleF6 = (event) => {
      if (event.key !== 'F6') return;
      if (detalheTransito) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      abrirDetalhesTransito();
    };

    window.addEventListener('keydown', handleF6, true);
    return () => window.removeEventListener('keydown', handleF6, true);
  }, [abrirDetalhesTransito, detalheTransito]);

  if (!podeAcessar) {
    return (
      <div className="content-card posicao-estoque-page">
        <div className="card-body">
          <div className="posicao-empty-state">
            <i className="fas fa-lock"></i>
            <p>Você não tem permissão para acessar esta página.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-card posicao-estoque-page">
      <div className="card-header">
        <h2><i className="fas fa-warehouse"></i> Posição de Estoques</h2>
        <p>Consulta consolidada por filial com histórico mensal de vendas e preços.</p>
      </div>

      <div className="card-body">
        <form onSubmit={carregarPosicao} className="consulta-form posicao-toolbar-form">
          <div className="consulta-form-group posicao-produto-group">
            <label>Produto</label>
            <div className="posicao-produto-actions" ref={buscaProdutoRef}>
              <input
                type="text"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                onFocus={() => {
                  if (sugestoesProduto.length) setMostrarSugestoesProduto(true);
                }}
                onKeyDown={async (e) => {
                  const textoAtual = String(codigo || '').trim();
                  const ehCodigo = pareceCodigoOuBarras(textoAtual);
                  if (e.key === 'Enter' && !ehCodigo && (!mostrarSugestoesProduto || !sugestoesProduto.length)) {
                    e.preventDefault();
                    if (sugestoesProduto.length > 0) setMostrarSugestoesProduto(true);
                    return;
                  }
                  if (!mostrarSugestoesProduto || !sugestoesProduto.length) return;
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setHighlightProdutoIndex((prev) => (prev + 1) % sugestoesProduto.length);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setHighlightProdutoIndex((prev) => (prev - 1 + sugestoesProduto.length) % sugestoesProduto.length);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    const item = sugestoesProduto[Math.max(0, Math.min(highlightProdutoIndex, sugestoesProduto.length - 1))];
                    if (item?.CDPRODU) {
                      const ok = await carregarPosicaoPorCodigo(item.CDPRODU);
                      if (ok) {
                        setCodigo('');
                        setSugestoesProduto([]);
                        setMostrarSugestoesProduto(false);
                        setHighlightProdutoIndex(0);
                      }
                    }
                  } else if (e.key === 'Escape') {
                    setMostrarSugestoesProduto(false);
                  }
                }}
                placeholder="Código interno, barras ou nome do produto"
                autoComplete="off"
              />
              <button type="submit" className="consulta-btn-submit posicao-submit-btn" disabled={loading}>
                <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'}`}></i>
                {loading ? 'Consultando...' : 'Consultar'}
              </button>
              {mostrarSugestoesProduto && (
                <div className="posicao-produto-dropdown">
                  {loadingBuscaProduto ? (
                    <div className="posicao-produto-dropdown-empty">Buscando produtos...</div>
                  ) : sugestoesProduto.length === 0 ? (
                    <div className="posicao-produto-dropdown-empty">Nenhum produto encontrado</div>
                  ) : (
                    sugestoesProduto.map((item, idx) => (
                      <button
                        key={`${item.CDPRODU}-${idx}`}
                        type="button"
                        className={`posicao-produto-option ${highlightProdutoIndex === idx ? 'active' : ''}`}
                        onMouseEnter={() => setHighlightProdutoIndex(idx)}
                        onClick={async () => {
                          const ok = await carregarPosicaoPorCodigo(item.CDPRODU);
                          if (ok) {
                            setCodigo('');
                            setSugestoesProduto([]);
                            setMostrarSugestoesProduto(false);
                            setHighlightProdutoIndex(0);
                          }
                        }}
                      >
                        <span className="posicao-produto-option-code">{item.CDPRODU}</span>
                        <span className="posicao-produto-option-name">{item.NOME}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="consulta-form-group posicao-filial-group">
            <label>Filial</label>
            {podeVerTodasFiliais ? (
              <CustomSelect
                options={[
                  { value: 'TODAS', label: 'Todas as Filiais' },
                  ...lojas.map((loja) => ({ value: String(loja.numero || loja.id), label: `${String(loja.numero || loja.id).padStart(2, '0')} - ${loja.nome}` }))
                ]}
                value={filial}
                onChange={setFilial}
                placeholder="Todas as Filiais"
                searchPlaceholder="Buscar loja..."
              />
            ) : (
              <div className="posicao-filial-readonly">
                {lojaUsuario ? `${String(lojaUsuario.numero || lojaUsuario.id).padStart(2, '0')} - ${lojaUsuario.nome}` : 'Filial do usuário'}
              </div>
            )}
          </div>
        </form>

        {erro && <div className="consulta-error posicao-feedback">{erro}</div>}
        {loading && (
          <div className="consulta-loading posicao-feedback">
            <i className="fas fa-spinner fa-spin"></i> Carregando posição de estoques...
          </div>
        )}

        {produto && (
          <div className="posicao-summary-panel">
            <div className="posicao-product-panel">
              <div className="code-chip">{produto.CDPRODU}</div>
              <div>
                <div className="posicao-product-name">{produto.NOME || 'Produto sem descrição'}</div>
                <div className="posicao-product-meta">
                  Barras: {produto.BARRA || produto.BAR1 || produto.BAR2 || produto.BAR3 || produto.BAR4 || '-'}
                  <span>Grupo: {produto.CDGRUPO || '-'}</span>
                  <span>Fornecedor: {produto.CDFORNE || '-'}</span>
                </div>
              </div>
            </div>

            <div className="posicao-inline-summary">
              <div className="posicao-inline-row">
                <div className="posicao-inline-item"><span>Preço de Venda:</span><strong>{money(resumoTopo.precoVenda)}</strong></div>
                <div className="posicao-inline-item"><span>Por (0,00 %):</span><strong>{money(resumoTopo.precoPor)}</strong></div>
                <div className="posicao-inline-item"><span>Custo Médio:</span><strong><span className="posicao-value-badge">{money(resumoTopo.custoMedio)}</span> -&gt; T{formatCompactNumber(resumoTopo.icms)}</strong></div>
                {!esconderEstoque && <div className="posicao-inline-item"><span>Estoq.:</span><strong>{integer(resumoTopo.estoqueTotal)}</strong></div>}
              </div>
              <div className="posicao-inline-row">
                <div className="posicao-inline-item"><span>Preço de Custo:</span><strong>{money(resumoTopo.precoCusto)}</strong></div>
                <div className="posicao-inline-item"><span>Margem:</span><strong>{formatPercent(resumoTopo.margem)}</strong></div>
                <div className="posicao-inline-item"><span>Embalagem:</span><strong>{resumoTopo.embalagem}</strong></div>
                <div className="posicao-inline-item"><span>Filiais:</span><strong>{integer(resumoTopo.filiais)}</strong></div>
              </div>
              <div className="posicao-inline-row">
                <div className="posicao-inline-item"><span>Preço Promoc.:</span><strong>{money(resumoTopo.precoPromo)}</strong></div>
                <div className="posicao-inline-item"><span>Preço de Fabrica:</span><strong>{money(resumoTopo.precoFabrica)}</strong></div>
                <div className="posicao-inline-item"><span>Margem Real:</span><strong>{formatPercent(resumoTopo.margemReal)} PMPF: {money(resumoTopo.pmpf)}</strong></div>
                <div className="posicao-inline-item"><span>Ped./Trans.:</span><strong>{integer(resumoTopo.pedidos)} / {integer(resumoTopo.transferencias)}</strong></div>
              </div>
            </div>
          </div>
        )}

        {!loading && posicoes.length > 0 && (
          <>
            <div className="excel-table-container posicao-excel-container">
              <table className="excel-table posicao-excel-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Filial</th>
                    <th>ABC</th>
                    <th className="text-right">Por R$</th>
                    <th className="text-right">Kit R$</th>
                    {monthColumns.map((month) => (
                      <th key={`${month.year}-${month.key}`} className="text-right">{monthLabel(month)}</th>
                    ))}
                    <th className="posicao-col-center">Objetivo</th>
                    <th className="posicao-col-center">Min.</th>
                    {!esconderEstoque && <th className="posicao-col-center">Estoq.</th>}
                    <th className="posicao-col-center">Robo</th>
                    <th className="posicao-col-center">D.Per.</th>
                    <th className="posicao-col-center">D.Men.</th>
                    <th className="posicao-col-center">Ped.</th>
                    <th className="posicao-col-center">Receb.</th>
                    <th className="posicao-col-center">Trans.</th>
                    <th className="text-center">Rupt</th>
                  </tr>
                </thead>
                <tbody>
                  {gruposTabela.filiaisNormais.map((row) => {
                    const histByYear = historicos?.[String(row.CDFIL)] || {};
                    const isSelected = String(row.CDFIL) === String(filialSelecionada);
                    return (
                      <tr key={row.CDFIL} className={`posicao-row ${isSelected ? 'selected' : 'clickable'}`} onClick={() => setFilialSelecionada(row.CDFIL)}>
                        <td className="sticky-col is-strong">{row.CDFIL} - {row.ABREV}</td>
                        <td className="text-center">{row.ABC || '-'}</td>
                        <td className="text-right currency">{money(row.PRECOPOR || row.PRECOMAX)}</td>
                        <td className="text-right currency">{money(row.PRECOKIT)}</td>
                        {monthColumns.map((month) => (
                          <td key={`${row.CDFIL}-${month.year}-${month.key}`} className="text-right number">{monthCell(histByYear, month)}</td>
                        ))}
                        <td className="posicao-col-center">{integer(row.OBJETIVOMES)}</td>
                        <td className="posicao-col-center">{integer(row.MINPRO || row.ESTMIN)}</td>
                        {!esconderEstoque && <td className="posicao-col-center posicao-col-estoq">{integer(row.ESTOQ)}</td>}
                        <td className="posicao-col-center">{integer(row.ESTOQUE_ROBO)}</td>
                        <td className="posicao-col-center">{integer(calcularDemandaPeriodo(row))}</td>
                        <td className="posicao-col-center">{integer(row.DEMMEN)}</td>
                        <td className="posicao-col-center">{integer(row.QTDPED)}</td>
                        <td className="posicao-col-center">{integer(row.QTDENT)}</td>
                        <td className="posicao-col-center">{integer(row.QTDTRN)}</td>
                        <td className="text-center">
                          <span className={`ruptura-flag ${String(row.ESTAEMRUPTURA || '').toUpperCase() === 'S' ? 'active' : ''}`}>
                            {String(row.ESTAEMRUPTURA || '').toUpperCase() === 'S' ? 'S' : 'N'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {gruposTabela.filiaisNormais.length > 0 && (
                    <tr className="posicao-total-row">
                      <td className="sticky-col is-strong">TOTAL FILIAIS</td>
                      <td></td><td></td><td></td>
                      {monthColumns.map((month) => {
                        const totalSaida = gruposTabela.filiaisNormais.reduce((acc, row) => {
                          const histByYear = historicos?.[String(row.CDFIL)] || {};
                          return acc + Number(readHistValue(histByYear, month.year, 'SAI', month.key) || 0);
                        }, 0);
                        return <td key={`total-filiais-${month.year}-${month.key}`} className="text-center">{integer(totalSaida)}</td>;
                      })}
                      <td className="posicao-col-center">{integer(gruposTabela.totaisNormais.OBJETIVOMES)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisNormais.MINPRO || gruposTabela.totaisNormais.ESTMIN)}</td>
                      {!esconderEstoque && <td className="posicao-col-center posicao-col-estoq">{integer(gruposTabela.totaisNormais.ESTOQ)}</td>}
                      <td className="posicao-col-center">{integer(gruposTabela.totaisNormais.ESTOQUE_ROBO)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisNormais.DPER)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisNormais.DEMMEN)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisNormais.QTDPED)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisNormais.QTDENT)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisNormais.QTDTRN)}</td>
                      <td className="text-center">{integer(gruposTabela.totaisNormais.RUPT_S)}</td>
                    </tr>
                  )}
                  {gruposTabela.filiaisSeparadas.map((row, idx) => {
                    const histByYear = historicos?.[String(row.CDFIL)] || {};
                    const isSelected = String(row.CDFIL) === String(filialSelecionada);
                    return (
                      <tr key={`especial-${row.CDFIL}-${idx}`} className={`posicao-row posicao-row-special ${isSelected ? 'selected' : 'clickable'} ${idx === 0 ? 'posicao-row-special-first' : ''}`} onClick={() => setFilialSelecionada(row.CDFIL)}>
                        <td className="sticky-col is-strong">{row.CDFIL} - {row.ABREV}</td>
                        <td className="text-center">{row.ABC || '-'}</td>
                        <td className="text-right currency">{money(row.PRECOPOR || row.PRECOMAX)}</td>
                        <td className="text-right currency">{money(row.PRECOKIT)}</td>
                        {monthColumns.map((month) => (
                          <td key={`especial-${row.CDFIL}-${month.year}-${month.key}`} className="text-right number">{monthCell(histByYear, month)}</td>
                        ))}
                        <td className="posicao-col-center">{integer(row.OBJETIVOMES)}</td>
                        <td className="posicao-col-center">{integer(row.MINPRO || row.ESTMIN)}</td>
                        {!esconderEstoque && <td className="posicao-col-center posicao-col-estoq">{integer(row.ESTOQ)}</td>}
                        <td className="posicao-col-center">{integer(row.ESTOQUE_ROBO)}</td>
                        <td className="posicao-col-center">{integer(calcularDemandaPeriodo(row))}</td>
                        <td className="posicao-col-center">{integer(row.DEMMEN)}</td>
                        <td className="posicao-col-center">{integer(row.QTDPED)}</td>
                        <td className="posicao-col-center">{integer(row.QTDENT)}</td>
                        <td className="posicao-col-center">{integer(row.QTDTRN)}</td>
                        <td className="text-center">
                          <span className={`ruptura-flag ${String(row.ESTAEMRUPTURA || '').toUpperCase() === 'S' ? 'active' : ''}`}>
                            {String(row.ESTAEMRUPTURA || '').toUpperCase() === 'S' ? 'S' : 'N'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {posicoes.length > 0 && (
                    <tr className="posicao-total-row posicao-total-geral-row">
                      <td className="sticky-col is-strong">TOTAL GERAL</td>
                      <td></td><td></td><td></td>
                      {monthColumns.map((month) => {
                        const totalSaida = posicoes.reduce((acc, row) => {
                          const histByYear = historicos?.[String(row.CDFIL)] || {};
                          return acc + Number(readHistValue(histByYear, month.year, 'SAI', month.key) || 0);
                        }, 0);
                        return <td key={`total-geral-${month.year}-${month.key}`} className="text-center">{integer(totalSaida)}</td>;
                      })}
                      <td className="posicao-col-center">{integer(gruposTabela.totaisGerais.OBJETIVOMES)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisGerais.MINPRO || gruposTabela.totaisGerais.ESTMIN)}</td>
                      {!esconderEstoque && <td className="posicao-col-center posicao-col-estoq">{integer(gruposTabela.totaisGerais.ESTOQ)}</td>}
                      <td className="posicao-col-center">{integer(gruposTabela.totaisGerais.ESTOQUE_ROBO)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisGerais.DPER)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisGerais.DEMMEN)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisGerais.QTDPED)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisGerais.QTDENT)}</td>
                      <td className="posicao-col-center">{integer(gruposTabela.totaisGerais.QTDTRN)}</td>
                      <td className="text-center">{integer(gruposTabela.totaisGerais.RUPT_S)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <PosicaoDetalhesTransito
        detalhe={detalheTransito}
        abaAtiva={detalheAba}
        setAbaAtiva={setDetalheAba}
        loading={detalheLoading}
        onClose={() => {
          setDetalheTransito(null);
          setDetalheLoading(false);
        }}
      />
    </div>
  );
};

export default PosicaoEstoques;
